import { Text, type Range } from '../../core';
import type { Decorate, EditorBag } from '../../types';
import { findInsertionAnchorId, hasPendingInsertionAnchors } from '../../analytics/insertionPaint';

/**
 * Anchors one leaf per insertion so the render that puts the content on screen is
 * attributable to the insertion that requested it — without touching the document.
 *
 * A decoration emits no operations, creates no history entry, fires no `onChange`, and is
 * unreachable by autosave, because it is computed during render and discarded after it.
 *
 * One leaf is enough: an insertion is a single transform, so it produces a single commit
 * and a single paint. Decorating every inserted leaf would mount N probes all measuring
 * the same frame.
 *
 * Known sample-loss path: `Editable` drops any plugin decoration intersecting an active
 * range mask, and the dictation pipeline masks the range holding in-flight hypothesis
 * text. An insertion anchored inside that range emits no ScreenUpdate mark, so the stage
 * and total measures are dropped for that insertion. That loses samples rather than
 * recording wrong ones, which is the same trade decision 3 makes for abandoned
 * insertions.
 */
export const getDecorate = (): Decorate => {
  return ([node, path], editorBag: EditorBag) => {
    // The common case by far. Keeps the per-node cost on every render to one size check.
    if (!hasPendingInsertionAnchors(editorBag.editor)) return [];
    if (!Text.isText(node)) return [];

    const insertionId = findInsertionAnchorId(editorBag.editor, path);
    if (insertionId == null) return [];

    const range: Range = {
      anchor: { path, offset: 0 },
      focus: { path, offset: node.text.length },
      insertionMarkId: insertionId,
    };

    return [range];
  };
};
